<template>
  <div class="page" :class="pageSizeClass">
    <!-- 헤더 -->
    <div class="header-section">
      <h1 class="journal-title">International Journal of Korean Applied Linguistics</h1>
      <!--p class="vol-info">{{ displayVolume }}</p-->
      <p class="vol-info">Vol. 4 (2025. 12)</p>
    </div>

    <!-- 구분선 -->
    <div class="header-divider"></div>

    <!-- 목차 제목 -->
    <div class="toc-header">
      <h2 class="toc-title">CONTENTS</h2>
      <p class="toc-title-ko">목 차</p>
    </div>

    <!-- 목차 목록 -->
    <div class="toc-list">
      <div
        v-for="(item, index) in contents"
        :key="index"
        class="toc-item"
      >
        <div class="toc-item-content">
          <p class="toc-item-title">{{ item.title }}</p>
          <p v-if="item.title_eng" class="toc-item-title-eng">{{ item.title_eng }}</p>
          <p class="toc-item-author">{{ item.authors }}</p>
        </div>
        <div class="toc-item-page">
          <span class="page-number">{{ item.page || '' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "article-table-of-contents",
  props: {
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    journal: {
      type: Object,
      required: false,
      default: () => {},
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    pageSize: {
      type: String,
      required: false,
      default: "A5",
    },
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a5";
    },
    displayVolume() {
      const vol = this.journal?.volume || "";
      const issue = this.journal?.issue || "";
      const year = this.journal?.year || new Date().getFullYear();
      const month = this.journal?.month || (new Date().getMonth() + 1);

      let volText = vol ? `Vol. ${vol}` : "";
      if (issue) volText += `, No. ${issue}`;
      volText += ` (${year}. ${month})`;

      return volText;
    },
    contents() {
      // items가 있으면 그걸 사용, 없으면 빈 배열
      if (this.items && this.items.length > 0) {
        // 빈페이지(category === 'empty')는 목차에서 제외
        return this.items
          .filter(item => item.category !== 'empty')
          .map(item => ({
            title: this.formatTitle(item.title, item.subtitle),
            title_eng: this.formatTitle(item.title_eng, item.subtitle_eng),
            authors: this.formatAuthors(item.authors),
            page: item.start_page || '',
          }));
      }
      return [];
    },
  },
  methods: {
    formatAuthors(authors) {
      if (!authors || !Array.isArray(authors)) return '';
      return authors.map(a => a.full_name || a.name_eng || '').filter(Boolean).join(', ');
    },
    formatTitle(title, subtitle) {
      const safeTitle = title || '';
      const safeSubtitle = subtitle || '';
      if (!safeSubtitle) return safeTitle;
      if (!safeTitle) return safeSubtitle;
      return `${safeTitle}: ${safeSubtitle}`;
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-font-color: #231815;
$secondary-font-color: #666666;
$background-color: #ffffff;

// A4 패딩 (Journal Standard)
$a4-vertical-padding: 70px;
$a4-horizontal-padding: 70px;

// A5 패딩 (Journal Standard)
$a5-vertical-padding: 50px;
$a5-padding-left: 55px; // Binding Inner
$a5-padding-right: 35px; // Binding Outer

$sans-font-family: "Sandoll GtNeoCond", "Noto Sans KR", sans-serif;

$a4-width: 794px;
$a4-height: 1122px;
$a5-width: 559px;
$a5-height: 794px;

.page {
  background-color: $background-color;
  position: relative;
  box-sizing: border-box;
  page-break-before: always;
  page-break-after: always;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .header-section {
      margin-bottom: 20px;
    }

    .journal-title {
      font-size: 20px;
    }

    .vol-info {
      font-size: 14px;
    }

    .header-divider {
      margin: 20px 0 30px 0;
    }

    .toc-header {
      margin-bottom: 40px;
    }

    .toc-title {
      font-size: 24px;
      letter-spacing: 4px;
    }

    .toc-title-ko {
      font-size: 16px;
      letter-spacing: 8px;
    }

    .toc-list {
      gap: 24px;
    }

    .toc-item {
      padding-bottom: 16px;
    }

    .toc-item-content {
      padding-right: 20px;
    }

    .toc-item-title {
      font-size: 14px;
    }

    .toc-item-title-eng {
      font-size: 12px;
    }

    .toc-item-author {
      font-size: 12px;
    }

    .page-number {
      font-size: 14px;
    }
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    padding: $a5-vertical-padding $a5-padding-right $a5-vertical-padding $a5-padding-left;

    .header-section {
      margin-bottom: 12px;
    }

    .journal-title {
      font-size: 14px;
    }

    .vol-info {
      font-size: 10px;
    }

    .header-divider {
      margin: 12px 0 20px 0;
    }

    .toc-header {
      margin-bottom: 25px;
    }

    .toc-title {
      font-size: 18px;
      letter-spacing: 3px;
    }

    .toc-title-ko {
      font-size: 12px;
      letter-spacing: 5px;
    }

    .toc-list {
      gap: 10px;
    }

    .toc-item {
      padding-bottom: 6px;
    }

    .toc-item-content {
      padding-right: 12px;
    }

    .toc-item-title {
      font-size: 11px;
    }

    .toc-item-title-eng {
      font-size: 9px;
    }

    .toc-item-author {
      font-size: 9px;
    }

    .page-number {
      font-size: 11px;
    }
  }
}

.header-section {
  text-align: center;
}

.journal-title {
  font-family: $sans-font-family;
  font-weight: 700;
  color: $primary-font-color;
  margin: 0 0 8px 0;
}

.vol-info {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $secondary-font-color;
  margin: 0;
}

.header-divider {
  width: 100%;
  height: 2px;
  background-color: $primary-font-color;
}

.toc-header {
  text-align: center;
}

.toc-title {
  font-family: $sans-font-family;
  font-weight: 700;
  color: $primary-font-color;
  margin: 0 0 8px 0;
}

.toc-title-ko {
  font-family: $sans-font-family;
  font-weight: 500;
  color: $secondary-font-color;
  margin: 0;
}

.toc-list {
  display: flex;
  flex-direction: column;
}

.toc-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px dotted $secondary-font-color;
}

.toc-item-content {
  flex: 1;
}

.toc-item-title {
  font-family: $sans-font-family;
  font-weight: 600;
  color: $primary-font-color;
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.toc-item-title-eng {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $secondary-font-color;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.toc-item-author {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $secondary-font-color;
  margin: 0;
}

.toc-item-page {
  flex-shrink: 0;
}

.page-number {
  font-family: $sans-font-family;
  font-weight: 600;
  color: $primary-font-color;
}

@media print {
  .page.a5 {
    padding: 32px 36px;
  }

  .page.a5 .toc-header {
    margin-bottom: 20px;
  }

  .page.a5 .toc-list {
    gap: 12px;
  }

  .page.a5 .toc-item {
    padding-bottom: 6px;
  }

  .page.a5 .toc-item-title {
    font-size: 10px;
    line-height: 1.35;
  }

  .page.a5 .toc-item-title-eng {
    font-size: 8px;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }

  .page.a5 .toc-item-author {
    font-size: 8px;
  }

  .page.a5 .page-number {
    font-size: 10px;
  }
}
</style>
